import { useEffect, useState } from 'react';
import { Smartphone, AlertTriangle, CheckCircle2 } from 'lucide-react';
import api from '../lib/api';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import ClaimNoteBody from './ClaimNoteBody';

const RESULT_STYLE = {
  short: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  missing: 'bg-rose-500/15 text-rose-600 border-rose-500/30',
  over: 'bg-sky-500/15 text-sky-600 border-sky-500/30',
};

const issueLines = (pc) => pc.lines.filter((l) => l.result !== 'match' || l.damaged > 0);

function PrecheckCard({ pc, latest }) {
  const issues = issueLines(pc);
  const [claimOpen, setClaimOpen] = useState(false);
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${latest ? '' : 'opacity-70'}`} data-testid={`po-precheck-${pc.precheck_id}`}>
      <div className="flex items-center justify-between gap-2 text-xs">
        <span className="flex items-center gap-1.5 font-medium">
          {pc.status === 'clean' ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />}
          {pc.status === 'clean' ? 'Everything matched' : `${pc.issue_count} issue(s) to chase`}
        </span>
        <span className="text-muted-foreground">{pc.checked_by} · {new Date(pc.created_at).toLocaleString()}</span>
      </div>
      {issues.map((l) => (
        <div key={l.product_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`po-precheck-issue-${pc.precheck_id}-${l.product_id}`}>
          <span className="truncate">{l.product_name}</span>
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="text-muted-foreground">expected {l.expected}, got {l.delivered}</span>
            {l.result !== 'match' && <Badge variant="outline" className={`text-[10px] uppercase ${RESULT_STYLE[l.result]}`}>{l.result}</Badge>}
            {l.damaged > 0 && <Badge variant="outline" className="text-[10px] uppercase bg-rose-500/15 text-rose-600 border-rose-500/30">{l.damaged} damaged</Badge>}
          </span>
        </div>
      ))}
      {pc.unexpected?.map((u) => (
        <div key={u.code} className="flex justify-between text-xs" data-testid={`po-precheck-unexpected-${pc.precheck_id}-${u.code}`}>
          <span className="truncate">{u.product_name || 'Unknown item'} ({u.code})</span>
          <Badge variant="outline" className="text-[10px] uppercase">not ordered × {u.quantity}</Badge>
        </div>
      ))}
      {pc.notes && <p className="text-xs italic text-muted-foreground">"{pc.notes}"</p>}
      {pc.issue_count > 0 && (
        <Button size="sm" variant="outline" onClick={() => setClaimOpen(true)} className="h-7 text-xs" data-testid={`po-precheck-claim-${pc.precheck_id}`}>Supplier claim note</Button>
      )}
      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent className="max-w-xl" data-testid="po-claim-dialog">
          <DialogHeader><DialogTitle>Supplier claim note — {pc.po_id}</DialogTitle></DialogHeader>
          {claimOpen && <ClaimNoteBody precheckId={pc.precheck_id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Delivery pre-checks captured on the Staff Companion app, shown on the desktop PO view. */
export default function POPrecheckPanel({ poId }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    setRows(null);
    api.get(`/staff-app/receiving/orders/${poId}/prechecks`).then((r) => setRows(r.data.prechecks)).catch(() => setRows([]));
  }, [poId]);
  if (!rows?.length) return null;
  return (
    <>
      <Separator />
      <div data-testid="po-precheck-panel">
        <p className="text-sm font-semibold mb-2 flex items-center gap-1.5"><Smartphone className="h-4 w-4" /> Delivery pre-checks</p>
        <div className="space-y-2">
          {rows.map((pc, i) => <PrecheckCard key={pc.precheck_id} pc={pc} latest={i === 0} />)}
        </div>
      </div>
    </>
  );
}

export function PrecheckBadge({ last }) {
  if (!last) return null;
  const clean = last.status === 'clean';
  return (
    <Badge variant="outline" data-testid="po-precheck-badge"
      className={`text-[10px] mt-1 ${clean ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' : 'bg-amber-500/10 text-amber-600 border-amber-500/30'}`}>
      {clean ? 'Pre-check: all match' : `Pre-check: ${last.issue_count} issue(s)`}
    </Badge>
  );
}
